import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { getNamedType, isObjectType, isInterfaceType, isUnionType, Kind } from "graphql";
import { useState } from "react";
import { fieldSegment, findDefinition, findSelectionSet } from "../lib/ast-path.js";
import { useFieldTreeContext } from "./field-tree-context.js";
import { FieldTreeList } from "./field-tree.js";
import { FieldRow } from "./field-row.js";
import { TypeConditionSelector } from "./type-condition-selector.js";
import { useCursorReveal } from "./use-cursor-reveal.js";
import { getFieldArgValues, getFieldArgVariables } from "../lib/arguments.js";
import { isFieldSelected } from "../lib/field-selection.js";
const FieldTreeNode = (t0) => {
  const $ = c(6);
  const {
    field,
    path
  } = t0;
  const {
    doc,
    schema,
    target,
    cursorPath,
    onToggle,
    onSetArg,
    onPromoteArg,
    onDemoteArg,
    onExtractFragment,
    onFocusFragment,
    onRemoveFragmentSpread
  } = useFieldTreeContext();
  const [expanded, setExpanded] = useState(false);
  const fullPath = [...path, fieldSegment(field.name)];
  const argValues = getFieldArgValues(doc, fullPath, target);
  const argVariables = getFieldArgVariables(doc, fullPath, target);
  let isInterface;
  let isObject;
  let namedType;
  let t1;
  if ($[0] !== field.type) {
    namedType = getNamedType(field.type);
    isObject = isObjectType(namedType);
    isInterface = isInterfaceType(namedType);
    t1 = namedType !== void 0 && (isUnionType(namedType) || isInterfaceType(namedType));
    $[0] = field.type;
    $[1] = isInterface;
    $[2] = isObject;
    $[3] = namedType;
    $[4] = t1;
  } else {
    isInterface = $[1];
    isObject = $[2];
    namedType = $[3];
    t1 = $[4];
  }
  const isAbstract = t1;
  const selected = isFieldSelected(doc, fullPath, target);
  const hasChildren = isObject || isAbstract;
  const isFragmentTarget = isObject || isInterface;
  const definition = findDefinition(doc, target);
  const nodeSelectionSet = definition ? findSelectionSet(definition.selectionSet, fullPath) : void 0;
  const nodeSelections = (nodeSelectionSet == null ? void 0 : nodeSelectionSet.selections) ?? [];
  const spreadRefs = nodeSelections.filter(_temp).map(_temp2);
  const hasExtractableSelection = nodeSelections.some(_temp3);
  const canExtract = Boolean(isFragmentTarget && namedType && onExtractFragment && expanded && hasExtractableSelection);
  const {
    flash,
    current,
    nodeRef
  } = useCursorReveal(fullPath, cursorPath, setExpanded);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleExpand2() {
      setExpanded(_temp4);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleExpand = t2;
  const handleExtract = canExtract && namedType ? () => onExtractFragment(fullPath, namedType.name) : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "graphiql-qb-field-node", ref: nodeRef, children: [
    /* @__PURE__ */ jsx(FieldRow, { field, path, selected, hasChildren, expanded, flash, current, argValues, argVariables, onExtractFragment: handleExtract, onToggle, onExpand: handleExpand, onSetArg, onPromoteArg, onDemoteArg }),
    expanded && spreadRefs.length > 0 && /* @__PURE__ */ jsx("ul", { className: "graphiql-qb-fragment-refs", role: "list", style: {
      paddingLeft: fullPath.length * 12
    }, children: spreadRefs.map((name) => /* @__PURE__ */ jsxs("li", { className: "graphiql-qb-fragment-ref-item", children: [
      onRemoveFragmentSpread && /* @__PURE__ */ jsx("input", { type: "checkbox", className: "graphiql-qb-field-checkbox", checked: true, onChange: () => onRemoveFragmentSpread(fullPath, name), "aria-label": `Remove fragment spread ${name}` }),
      onFocusFragment ? /* @__PURE__ */ jsxs("button", { type: "button", className: "graphiql-qb-fragment-ref graphiql-qb-fragment-ref--button", "data-testid": "fragment-ref", onClick: () => onFocusFragment(name), "aria-label": `Edit fragment ${name}`, children: [
        /* @__PURE__ */ jsx("span", { className: "graphiql-qb-spread", children: "..." }),
        name
      ] }) : /* @__PURE__ */ jsxs("span", { className: "graphiql-qb-fragment-ref", "data-testid": "fragment-ref", children: [
        /* @__PURE__ */ jsx("span", { className: "graphiql-qb-spread", children: "..." }),
        name
      ] })
    ] }, name)) }),
    isObject && expanded && namedType && isObjectType(namedType) && /* @__PURE__ */ jsx(FieldTreeList, { type: namedType, path: fullPath }),
    isAbstract && expanded && /* @__PURE__ */ jsxs(Fragment, { children: [
      namedType && isInterfaceType(namedType) && /* @__PURE__ */ jsx(FieldTreeList, { type: namedType, path: fullPath }),
      schema && namedType && /* @__PURE__ */ jsx(TypeConditionSelector, { fieldPath: fullPath, abstractType: namedType })
    ] })
  ] });
};
function _temp(s) {
  return s.kind === Kind.FRAGMENT_SPREAD;
}
function _temp2(s_0) {
  return s_0.name.value;
}
function _temp3(s_1) {
  return s_1.kind === Kind.FIELD || s_1.kind === Kind.INLINE_FRAGMENT;
}
function _temp4(prev) {
  return !prev;
}
export {
  FieldTreeNode
};
//# sourceMappingURL=field-tree-node.js.map
